import { TFunction } from 'i18next';
import { useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { useAnalyzeShapefile } from '@/api/rest/resources/analyzeShapefile';
import { FRONTEND_MAX_UPLOAD_FILE_SIZE_MB } from '@/config/constants';

import { autoMapColumn, LANDLER_FIELDS, LandlerField, UNKNOWN_ERROR_KEY } from '../utils';

type UseShapefileDropzoneParams = {
  disabled: boolean;
  t: TFunction;
  onDropAccepted: () => void;
};

export const useShapefileDropzone = ({ disabled, t, onDropAccepted }: UseShapefileDropzoneParams) => {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [detectedColumns, setDetectedColumns] = useState<string[]>([]);
  const [columnMappings, setColumnMappings] = useState<Map<string, LandlerField>>(new Map());

  const { submit: analyzeShapefile } = useAnalyzeShapefile();

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    accept: {
      'application/vnd.google-earth.kml+xml': ['.kml'],
      'application/zip': ['.zip'],
      'application/vnd.google-earth.kmz': ['.kmz'],
    },
    maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024,
    maxFiles: 1,
    disabled,
    onDrop: async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;
      setPendingFile(file);
      onDropAccepted();
      setIsAnalyzing(true);
      try {
        const { columns } = await analyzeShapefile({ file });
        setDetectedColumns(columns);
        setColumnMappings(new Map(columns.map((col) => [col, autoMapColumn(col)] as [string, LandlerField])));
      } catch {
        setDetectedColumns([]);
        setColumnMappings(new Map());
      } finally {
        setIsAnalyzing(false);
      }
    },
  });

  const fileErrors = useMemo(
    () =>
      Array.from(
        new Set(
          fileRejections
            .flatMap((r) => r.errors)
            .map((err) => {
              if (err.code === 'file-invalid-type') return t('shared.uploadPlots.error.file-invalid-type');
              if (err.code === 'file-too-large')
                return t('shared.uploadPlots.error.shapefileFileTooLarge', {
                  maxSize: FRONTEND_MAX_UPLOAD_FILE_SIZE_MB,
                });
              if (err.code === 'too-many-files') return t('shared.uploadPlots.error.too-many-files');
              return t(UNKNOWN_ERROR_KEY);
            }),
        ),
      ),
    [fileRejections, t],
  );

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    fileErrors,
    isAnalyzing,
    pendingFile,
    detectedColumns,
    columnMappings,
    setColumnMappings,
    landlerFields: LANDLER_FIELDS,
  };
};
